import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, Trash2, Send, Truck, User, Calendar, Clock, CreditCard, Wallet } from "lucide-react";

export default function VendedorVenta() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [productos, setProductos] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [configLanding, setConfigLanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cart, setCart] = useState([]);
  
  // Digital vendors only have "tercero" type, Carrito vendors can choose
  const isDigital = user?.tipo_vendedor === "digital";
  const [tipoVenta, setTipoVenta] = useState("directa");
  
  const [form, setForm] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    cliente_direccion: "",
    cliente_barrio: "",
    fecha_entrega: "",
    hora_entrega: "",
    tipo_recaudo: "contra_entrega",
    metodo_pago: "efectivo",
    tipo_entrega: "domicilio", // "domicilio" o "recoger"
    notas: "",
  });

  // Update tipoVenta when user loads
  useEffect(() => {
    if (user?.tipo_vendedor === "digital") {
      setTipoVenta("tercero");
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Always fetch productos and config - inventory is optional
      const [productosRes, configRes] = await Promise.all([
        api.get("/productos"),
        api.get("/configuracion/landing"),
      ]);
      
      setProductos(productosRes.data.filter((p) => p.activo));
      setConfigLanding(configRes.data);
      
      // Try to fetch inventory - but don't fail if it errors
      try {
        const inventarioRes = await api.get("/inventario");
        setInventario(inventarioRes.data || []);
      } catch (invError) {
        console.log("No inventory available or error fetching:", invError);
        setInventario([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const getStock = (productoId) => {
    const inv = inventario.find((i) => i.producto_id === productoId);
    return inv?.cantidad_disponible || 0;
  };

  const addToCart = (producto) => {
    const stock = getStock(producto.id);
    const existing = cart.find((c) => c.producto_id === producto.id);
    const currentQty = existing?.cantidad || 0;

    // Digital sellers and third-party orders have no inventory restrictions
    // Only direct sales from Carrito sellers check inventory
    const noStockRestriction = isDigital || tipoVenta === "tercero";
    if (!noStockRestriction && currentQty >= stock) {
      toast.error("No hay más stock disponible");
      return;
    }

    if (existing) {
      setCart(
        cart.map((c) =>
          c.producto_id === producto.id
            ? { ...c, cantidad: c.cantidad + 1, subtotal: (c.cantidad + 1) * c.precio_unitario }
            : c
        )
      );
    } else {
      setCart([
        ...cart,
        {
          producto_id: producto.id,
          producto_nombre: producto.nombre,
          cantidad: 1,
          precio_unitario: producto.precio,
          subtotal: producto.precio,
        },
      ]);
    }
  };

  const updateQuantity = (productoId, delta) => {
    const stock = getStock(productoId);
    // Digital sellers and third-party orders have no stock limit
    const noStockRestriction = isDigital || tipoVenta === "tercero";
    
    setCart(
      cart
        .map((c) => {
          if (c.producto_id === productoId) {
            const maxQty = noStockRestriction ? Infinity : stock;
            const newQty = Math.max(0, Math.min(c.cantidad + delta, maxQty));
            return newQty === 0
              ? null
              : { ...c, cantidad: newQty, subtotal: newQty * c.precio_unitario };
          }
          return c;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (productoId) => {
    setCart(cart.filter((c) => c.producto_id !== productoId));
  };

  const subtotal = cart.reduce((sum, c) => sum + c.subtotal, 0);
  // Solo cobrar domicilio si es tipo tercero Y tipo_entrega es domicilio
  const costoDomicilio = (tipoVenta === "tercero" && form.tipo_entrega === "domicilio") 
    ? (configLanding?.costo_domicilio || 0) 
    : 0;
  const total = subtotal + costoDomicilio;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Agrega productos al carrito");
      return;
    }

    // Validate required fields for third-party orders
    if (tipoVenta === "tercero") {
      if (!form.cliente_nombre || !form.cliente_telefono) {
        toast.error("Para pedidos a tercero, debes completar nombre y teléfono");
        return;
      }
      // Solo requerir dirección si es delivery
      if (form.tipo_entrega === "domicilio" && !form.cliente_direccion) {
        toast.error("Para delivery, debes indicar la dirección de entrega");
        return;
      }
      if (!form.fecha_entrega || !form.hora_entrega) {
        toast.error("Debes indicar la fecha y hora de entrega");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (tipoVenta === "tercero") {
        // Create third-party order
        await api.post("/pedidos-terceros", {
          items: cart,
          cliente_nombre: form.cliente_nombre,
          cliente_telefono: form.cliente_telefono,
          cliente_direccion: form.tipo_entrega === "domicilio" ? form.cliente_direccion : null,
          cliente_barrio: form.cliente_barrio || null,
          fecha_entrega: form.fecha_entrega,
          hora_entrega: form.hora_entrega,
          tipo_recaudo: form.tipo_recaudo,
          metodo_pago: form.metodo_pago,
          tipo_entrega: form.tipo_entrega,
          notas: form.notas || null,
        });
        
        const mensajeTipo = form.tipo_recaudo === "pago_vendedor" 
          ? "Pedido creado (Pagado - Pendiente de entrega)" 
          : "Pedido creado (Pago contra entrega)";
        toast.success(mensajeTipo);
        navigate("/vendedor/pedidos");
      } else {
        // Regular direct sale
        const res = await api.post("/ventas", {
          items: cart,
          cliente_nombre: form.cliente_nombre || null,
          cliente_telefono: form.cliente_telefono || null,
          metodo_pago: form.metodo_pago,
          notas: form.notas || null,
        });
        if (res.data?.alerta_inventario) {
          const det = (res.data.productos_excedidos || []).map((p) => `${p.producto_nombre} (disponible: ${p.disponible})`).join(", ");
          toast.warning(`⚠️ Venta registrada, pero excede tu inventario: ${det}. El administrador fue notificado.`, { duration: 8000 });
        } else {
          toast.success("¡Venta registrada!");
        }
        navigate("/vendedor");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-venta">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Nueva Venta</h1>
        <p className="text-torta-muted">Registra una nueva venta</p>
      </div>

      {/* Tipo de Venta Selector - Solo para vendedores Carrito */}
      {!isDigital && (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setTipoVenta("directa")}
            className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
              tipoVenta === "directa"
                ? "border-torta-brown bg-torta-cream text-torta-dark"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              tipoVenta === "directa" ? "bg-torta-brown text-white" : "bg-gray-100"
            }`}>
              <User className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium">Venta Directa</p>
              <p className="text-xs opacity-70">Cliente presente</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTipoVenta("tercero")}
            className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
              tipoVenta === "tercero"
                ? "border-blue-500 bg-blue-50 text-blue-800"
                : "border-gray-200 text-gray-500 hover:border-gray-300"
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              tipoVenta === "tercero" ? "bg-blue-500 text-white" : "bg-gray-100"
            }`}>
              <Truck className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-medium">Pedido a Tercero</p>
              <p className="text-xs opacity-70">Entrega a domicilio</p>
            </div>
          </button>
        </div>
      )}

      {/* Header for Digital vendors - Simplified title */}
      {isDigital && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium text-blue-800">Nuevo Pedido</p>
            <p className="text-blue-600 text-sm">Entrega a domicilio desde central</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Products */}
        <div className="space-y-4">
          <h2 className="font-medium text-torta-dark">Productos</h2>
          <div className="grid gap-3">
            {productos.map((p) => {
              const stock = getStock(p.id);
              const inCart = cart.find((c) => c.producto_id === p.id)?.cantidad || 0;
              // Digital sellers and third-party orders have no stock restrictions
              const noStockRestriction = isDigital || tipoVenta === "tercero";
              const canAddMore = noStockRestriction || inCart < stock;
              const canAdd = noStockRestriction || stock > 0;

              return (
                <div
                  key={p.id}
                  className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-torta-dark">{p.nombre}</p>
                    <p className="text-sm text-torta-muted">{formatCurrency(p.precio)}</p>
                    {/* Only show stock for Carrito sellers doing direct sales */}
                    {!isDigital && tipoVenta !== "tercero" && (
                      <p className={`text-xs ${stock > 0 ? "text-green-600" : "text-red-600"}`}>
                        Stock: {stock} und
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {inCart > 0 ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(p.id, -1)}
                          className="w-8 h-8 p-0"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{inCart}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(p.id, 1)}
                          disabled={!canAddMore}
                          className="w-8 h-8 p-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => addToCart(p)}
                        disabled={!canAdd}
                        className="bg-torta-brown hover:bg-torta-brown/90"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cart & Form */}
        <div className="space-y-4">
          {/* Cart */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-medium text-torta-dark mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-torta-brown" />
              Carrito
            </h2>

            {cart.length === 0 ? (
              <p className="text-torta-muted text-center py-4">Carrito vacío</p>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div
                    key={item.producto_id}
                    className="flex items-center justify-between bg-torta-cream/50 rounded-lg p-3"
                  >
                    <div>
                      <p className="font-medium text-torta-dark text-sm">
                        {item.cantidad}x {item.producto_nombre}
                      </p>
                      <p className="text-xs text-torta-muted">
                        {formatCurrency(item.precio_unitario)} c/u
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-torta-brown">
                        {formatCurrency(item.subtotal)}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.producto_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                <div className="border-t border-torta-sand pt-3 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-torta-muted">Subtotal productos</span>
                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  {tipoVenta === "tercero" && costoDomicilio > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-torta-muted">Domicilio</span>
                      <span className="font-medium">{formatCurrency(costoDomicilio)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-torta-sand">
                    <span className="font-medium text-torta-dark">Total</span>
                    <span className="font-display text-2xl text-torta-brown">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            {/* Show header based on type */}
            {tipoVenta === "tercero" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                <p className="text-blue-800 text-sm font-medium flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Pedido a Tercero - Datos obligatorios
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{tipoVenta === "tercero" ? "Cliente *" : "Cliente (opcional)"}</Label>
                <Input
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })}
                  placeholder="Nombre"
                  required={tipoVenta === "tercero"}
                />
              </div>
              <div className="space-y-2">
                <Label>{tipoVenta === "tercero" ? "Teléfono *" : "Teléfono (opcional)"}</Label>
                <Input
                  value={form.cliente_telefono}
                  onChange={(e) => setForm({ ...form, cliente_telefono: e.target.value })}
                  placeholder="300 123 4567"
                  required={tipoVenta === "tercero"}
                  data-testid="venta-telefono-input"
                />
                <p className="text-[11px] text-amber-700">👑 Con el celular, la compra suma al Club de la Abuela del cliente</p>
              </div>
            </div>

            {/* Delivery options for third-party orders */}
            {tipoVenta === "tercero" && (
              <div className="space-y-4 pt-2 border-t border-gray-100">
                {/* Tipo de entrega */}
                <div className="space-y-2">
                  <Label>Tipo de Entrega *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_entrega: "domicilio" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_entrega === "domicilio"
                          ? "border-blue-500 bg-blue-50 text-blue-800"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <Truck className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Domicilio</p>
                        <p className="text-xs opacity-70">+{formatCurrency(configLanding?.costo_domicilio || 0)}</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_entrega: "recoger" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_entrega === "recoger"
                          ? "border-purple-500 bg-purple-50 text-purple-800"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <User className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Recoger</p>
                        <p className="text-xs opacity-70">Sin costo adicional</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Address fields - only show if delivery */}
                {form.tipo_entrega === "domicilio" && (
                  <>
                    <div className="space-y-2">
                      <Label>Dirección de entrega *</Label>
                      <Input
                        value={form.cliente_direccion}
                        onChange={(e) => setForm({ ...form, cliente_direccion: e.target.value })}
                        placeholder="Calle, número, edificio, apto..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Barrio (opcional)</Label>
                      <Input
                        value={form.cliente_barrio}
                        onChange={(e) => setForm({ ...form, cliente_barrio: e.target.value })}
                        placeholder="Nombre del barrio"
                      />
                    </div>
                  </>
                )}

                {form.tipo_entrega === "recoger" && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-purple-800 text-sm">
                      <strong>Recoger en punto:</strong> El cliente recogerá el pedido contigo o en el punto de venta acordado.
                    </p>
                  </div>
                )}

                {/* Fecha y hora de entrega */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Fecha de entrega *
                    </Label>
                    <Input
                      type="date"
                      value={form.fecha_entrega}
                      onChange={(e) => setForm({ ...form, fecha_entrega: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Hora de entrega *
                    </Label>
                    <Input
                      type="time"
                      value={form.hora_entrega}
                      onChange={(e) => setForm({ ...form, hora_entrega: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Tipo de recaudo */}
                <div className="space-y-2">
                  <Label>Tipo de Recaudo *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_recaudo: "pago_vendedor" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_recaudo === "pago_vendedor"
                          ? "border-green-500 bg-green-50 text-green-800"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <Wallet className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Pago a Vendedor</p>
                        <p className="text-xs opacity-70">Cliente ya pagó</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_recaudo: "contra_entrega" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_recaudo === "contra_entrega"
                          ? "border-amber-500 bg-amber-50 text-amber-800"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <CreditCard className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Contra Entrega</p>
                        <p className="text-xs opacity-70">Paga al recibir</p>
                      </div>
                    </button>
                  </div>
                </div>

                {form.tipo_recaudo === "pago_vendedor" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-800 text-sm">
                      <strong>Pago a vendedor:</strong> Este monto se incluirá en tu cierre de caja de hoy.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Método de pago</Label>
              <Select
                value={form.metodo_pago}
                onValueChange={(value) => setForm({ ...form, metodo_pago: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Observaciones..."
                rows={2}
              />
            </div>

            <Button
              type="submit"
              disabled={cart.length === 0 || submitting}
              className={`w-full h-12 ${
                tipoVenta === "tercero" 
                  ? "bg-blue-600 hover:bg-blue-700" 
                  : "bg-torta-brown hover:bg-torta-brown/90"
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {tipoVenta === "tercero" ? "Creando pedido..." : "Registrando..."}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {tipoVenta === "tercero" ? <Truck className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                  {tipoVenta === "tercero" 
                    ? `Crear Pedido - ${formatCurrency(total)}` 
                    : `Registrar Venta - ${formatCurrency(total)}`}
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
